/**
 * FormulaEngine - Basic formula parser and evaluator for SheetsApp_v2
 * Supports common spreadsheet functions and operations
 */

import { FormulaError } from './SheetsConfig';
// Removed unused imports - functions implemented locally if needed

/**
 * Formula evaluation result
 */
export interface FormulaResult {
  value: any;
  error?: FormulaError;
  dependencies: string[]; // Cell references this formula depends on
}

/**
 * Cell reference for formula evaluation
 */
export interface CellRef {
  row: number;
  col: number;
  value: any;
}

/**
 * Formula Engine - Parses and evaluates spreadsheet formulas
 */
export class FormulaEngine {
  private cellValues: Map<string, any>; // cellRef (e.g., "A1") -> value

  constructor() {
    this.cellValues = new Map();
  }

  /**
   * Set cell value for formula evaluation
   */
  public setCellValue(cellRef: string, value: any): void {
    this.cellValues.set(cellRef.toUpperCase(), value);
  }

  /**
   * Get cell value
   */
  public getCellValue(cellRef: string): any {
    return this.cellValues.get(cellRef.toUpperCase());
  }

  /**
   * Clear all cell values
   */
  public clear(): void {
    this.cellValues.clear();
  }

  /**
   * Evaluate a formula
   */
  public evaluate(formula: string): FormulaResult {
    try {
      // Remove leading "=" if present
      const cleanFormula = formula.trim().startsWith('=') 
        ? formula.trim().substring(1) 
        : formula.trim();

      const dependencies: string[] = [];
      
      // Extract cell references (A1, B2, etc.) and ranges (A1:B10)
      const cellRefPattern = /\b[A-Z]+[0-9]+\b/g;
      const rangePattern = /\b[A-Z]+[0-9]+:[A-Z]+[0-9]+\b/g;
      
      // Extract ranges first
      const rangeMatches = cleanFormula.match(rangePattern);
      if (rangeMatches) {
        rangeMatches.forEach(range => {
          // Expand range to individual cells for dependencies
          const [start, end] = range.split(':');
          const startPos = this.parseA1Notation(start);
          const endPos = this.parseA1Notation(end);
          
          for (let r = startPos.row; r <= endPos.row; r++) {
            for (let c = startPos.col; c <= endPos.col; c++) {
              dependencies.push(this.toA1Notation(r, c));
            }
          }
        });
      }
      
      // Extract individual cell references
      const cellMatches = cleanFormula.match(cellRefPattern);
      if (cellMatches) {
        // ✅ FIX #6: Filter out function names (they're followed by '(')
        const filteredRefs = cellMatches.filter(ref => {
          const refIndex = cleanFormula.indexOf(ref);
          const charAfter = cleanFormula[refIndex + ref.length];
          // If followed by '(', it's a function call, not a cell reference
          return charAfter !== '(';
        });
        dependencies.push(...filteredRefs.map(ref => ref.toUpperCase()));
      }

      // Evaluate the formula
      const value = this.evaluateExpression(cleanFormula);

      return {
        value,
        dependencies: Array.from(new Set(dependencies)) // Remove duplicates
      };
    } catch (error) {
      return {
        value: null,
        error: FormulaError.VALUE,
        dependencies: []
      };
    }
  }

  /**
   * Evaluate expression (recursive)
   */
  private evaluateExpression(expr: string): any {
    expr = expr.trim();

    // Check for range (A1:B10)
    if (this.isRange(expr)) {
      return this.expandRange(expr);
    }

    // Check for function calls
    if (expr.includes('(')) {
      return this.evaluateFunction(expr);
    }

    // Check for operations
    if (this.hasOperator(expr)) {
      return this.evaluateOperation(expr);
    }

    // Check for cell reference
    if (/^[A-Z]+[0-9]+$/.test(expr)) {
      return this.evaluateCellRef(expr);
    }

    // Check for number
    if (!isNaN(Number(expr))) {
      return Number(expr);
    }

    // Check for string (quoted)
    if ((expr.startsWith('"') && expr.endsWith('"')) || 
        (expr.startsWith("'") && expr.endsWith("'"))) {
      return expr.slice(1, -1);
    }

    // Check for boolean
    if (expr.toUpperCase() === 'TRUE') return true;
    if (expr.toUpperCase() === 'FALSE') return false;

    throw new Error('Invalid expression');
  }
  
  /**
   * Check if expression is a range (A1:B10)
   */
  private isRange(expr: string): boolean {
    return /^[A-Z]+[0-9]+:[A-Z]+[0-9]+$/.test(expr);
  }
  
  /**
   * Expand range to array of values
   * A1:A5 → [valueA1, valueA2, valueA3, valueA4, valueA5]
   */
  private expandRange(range: string): any[] {
    const [start, end] = range.split(':');
    const startPos = this.parseA1Notation(start);
    const endPos = this.parseA1Notation(end);
    
    const values: any[] = [];
    for (let r = startPos.row; r <= endPos.row; r++) {
      for (let c = startPos.col; c <= endPos.col; c++) {
        const cellRef = this.toA1Notation(r, c);
        const value = this.getCellValue(cellRef);
        values.push(value !== undefined ? value : '');
      }
    }
    
    return values;
  }
  
  /**
   * Parse A1 notation to row/col
   */
  private parseA1Notation(ref: string): { row: number; col: number } {
    const match = ref.match(/^([A-Z]+)([0-9]+)$/);
    if (!match) throw new Error(`Invalid cell reference: ${ref}`);
    
    const colStr = match[1];
    const rowStr = match[2];
    
    // Convert column letters to number (A=0, B=1, ..., Z=25, AA=26, etc.)
    let col = 0;
    for (let i = 0; i < colStr.length; i++) {
      col = col * 26 + (colStr.charCodeAt(i) - 65 + 1);
    }
    col -= 1; // 0-indexed
    
    const row = parseInt(rowStr, 10) - 1; // 0-indexed
    
    return { row, col };
  }
  
  /**
   * Convert row/col to A1 notation
   */
  private toA1Notation(row: number, col: number): string {
    let colStr = '';
    let c = col + 1; // 1-indexed for conversion
    
    while (c > 0) {
      const remainder = (c - 1) % 26;
      colStr = String.fromCharCode(65 + remainder) + colStr;
      c = Math.floor((c - 1) / 26);
    }
    
    return `${colStr}${row + 1}`;
  }

  /**
   * Evaluate function call
   */
  private evaluateFunction(expr: string): any {
    const fnMatch = expr.match(/^([A-Z]+)\((.*)\)$/i);
    if (!fnMatch) throw new Error('Invalid function');

    const fnName = fnMatch[1].toUpperCase();
    const argsStr = fnMatch[2];
    const args = this.parseArgs(argsStr);

    // Evaluate each argument
    const evaluatedArgs = args.map(arg => this.evaluateExpression(arg));

    // Execute function
    switch (fnName) {
      // ========== Math/Aggregation Functions ==========
      case 'SUM':
        return this.flattenArgs(evaluatedArgs)
          .map(v => Number(v))
          .filter(v => !isNaN(v))
          .reduce((sum, val) => sum + val, 0);
      
      case 'AVERAGE':
      case 'AVG': {
        const numbers = this.flattenArgs(evaluatedArgs)
          .map(v => Number(v))
          .filter(v => !isNaN(v));
        return numbers.length > 0 ? numbers.reduce((s, v) => s + v, 0) / numbers.length : 0;
      }
      
      case 'MIN':
        return Math.min(...this.flattenArgs(evaluatedArgs).map(v => Number(v)).filter(v => !isNaN(v)));
      
      case 'MAX':
        return Math.max(...this.flattenArgs(evaluatedArgs).map(v => Number(v)).filter(v => !isNaN(v)));
      
      case 'COUNT':
        return this.flattenArgs(evaluatedArgs).filter(v => v !== '' && v !== null && v !== undefined).length;
      
      case 'COUNTA':
        return this.flattenArgs(evaluatedArgs).filter(v => v !== '' && v !== null && v !== undefined).length;
      
      case 'COUNTIF': {
        const range = this.flattenArgs([evaluatedArgs[0]]);
        const criteria = String(evaluatedArgs[1]);
        return this.countIf(range, criteria);
      }
      
      case 'SUMIF': {
        const criteriaRange = this.flattenArgs([evaluatedArgs[0]]);
        const criteria = String(evaluatedArgs[1]);
        const sumRange = evaluatedArgs[2] ? this.flattenArgs([evaluatedArgs[2]]) : criteriaRange;
        return this.sumIf(criteriaRange, criteria, sumRange);
      }
      
      case 'AVERAGEIF': {
        const criteriaRange = this.flattenArgs([evaluatedArgs[0]]);
        const criteria = String(evaluatedArgs[1]);
        const avgRange = evaluatedArgs[2] ? this.flattenArgs([evaluatedArgs[2]]) : criteriaRange;
        return this.averageIf(criteriaRange, criteria, avgRange);
      }
      
      // ========== Logical Functions ==========
      case 'IF':
        return Boolean(evaluatedArgs[0]) ? evaluatedArgs[1] : evaluatedArgs[2];
      
      case 'AND':
        return evaluatedArgs.every(arg => Boolean(arg));
      
      case 'OR':
        return evaluatedArgs.some(arg => Boolean(arg));
      
      case 'NOT':
        return !Boolean(evaluatedArgs[0]);
      
      case 'IFERROR':
        try {
          return evaluatedArgs[0];
        } catch {
          return evaluatedArgs[1];
        }
      
      case 'ISBLANK':
        return evaluatedArgs[0] === '' || evaluatedArgs[0] === null || evaluatedArgs[0] === undefined;
      
      case 'ISNUMBER':
        return !isNaN(Number(evaluatedArgs[0])) && evaluatedArgs[0] !== '';
      
      case 'ISTEXT':
        return typeof evaluatedArgs[0] === 'string' && evaluatedArgs[0] !== '';
      
      // ========== Math Functions ==========
      case 'ROUND':
        return Math.round(Number(evaluatedArgs[0]) * Math.pow(10, Number(evaluatedArgs[1] || 0))) / Math.pow(10, Number(evaluatedArgs[1] || 0));
      
      case 'ABS':
        return Math.abs(Number(evaluatedArgs[0]));
      
      case 'SQRT':
        return Math.sqrt(Number(evaluatedArgs[0]));
      
      case 'POW':
      case 'POWER':
        return Math.pow(Number(evaluatedArgs[0]), Number(evaluatedArgs[1]));
      
      // ========== Text Functions ==========
      case 'CONCAT':
      case 'CONCATENATE':
        return this.flattenArgs(evaluatedArgs).join('');
      
      case 'UPPER':
        return String(evaluatedArgs[0]).toUpperCase();
      
      case 'LOWER':
        return String(evaluatedArgs[0]).toLowerCase();
      
      case 'LEN':
      case 'LENGTH':
        return String(evaluatedArgs[0]).length;
      
      case 'LEFT':
        return String(evaluatedArgs[0]).substring(0, Number(evaluatedArgs[1] || 1));
      
      case 'RIGHT': {
        const text = String(evaluatedArgs[0]);
        const count = Number(evaluatedArgs[1] || 1);
        return text.substring(text.length - count);
      }
      
      case 'MID':
        return String(evaluatedArgs[0]).substring(
          Number(evaluatedArgs[1]) - 1,
          Number(evaluatedArgs[1]) - 1 + Number(evaluatedArgs[2])
        );
      
      case 'TRIM':
        return String(evaluatedArgs[0]).trim();
      
      case 'SUBSTITUTE':
        return String(evaluatedArgs[0]).replace(
          new RegExp(String(evaluatedArgs[1]).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
          String(evaluatedArgs[2])
        );
      
      // ========== Date/Time Functions ==========
      case 'TODAY':
        return new Date().toLocaleDateString('en-US');
      
      case 'NOW':
        return new Date().toLocaleString('en-US');
      
      case 'DATE': {
        const year = Number(evaluatedArgs[0]);
        const month = Number(evaluatedArgs[1]);
        const day = Number(evaluatedArgs[2]);
        return new Date(year, month - 1, day).toLocaleDateString('en-US');
      }
      
      case 'YEAR':
        return new Date(evaluatedArgs[0]).getFullYear();
      
      case 'MONTH':
        return new Date(evaluatedArgs[0]).getMonth() + 1;
      
      case 'DAY':
        return new Date(evaluatedArgs[0]).getDate();
      
      case 'WEEKDAY':
        return new Date(evaluatedArgs[0]).getDay() + 1; // 1=Sunday, 7=Saturday
      
      case 'DATEDIF': {
        const startDate = new Date(evaluatedArgs[0]);
        const endDate = new Date(evaluatedArgs[1]);
        const unit = String(evaluatedArgs[2]).toUpperCase();
        const diff = endDate.getTime() - startDate.getTime();
        
        switch (unit) {
          case 'D': return Math.floor(diff / (1000 * 60 * 60 * 24));
          case 'M': return (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
          case 'Y': return endDate.getFullYear() - startDate.getFullYear();
          default: return 0;
        }
      }
      
      // ========== Lookup Functions ==========
      case 'VLOOKUP': {
        const lookupValue = evaluatedArgs[0];
        const tableArray = this.flattenArgs([evaluatedArgs[1]]);
        const colIndex = Number(evaluatedArgs[2]);
        const exactMatch = evaluatedArgs[3] === undefined ? true : Boolean(evaluatedArgs[3]);
        return this.vlookup(lookupValue, tableArray, colIndex, exactMatch);
      }
      
      default:
        throw new Error(`Unknown function: ${fnName}`);
    }
  }
  
  /**
   * Flatten nested arrays (for range arguments)
   */
  private flattenArgs(args: any[]): any[] {
    const result: any[] = [];
    for (const arg of args) {
      if (Array.isArray(arg)) {
        result.push(...this.flattenArgs(arg));
      } else {
        result.push(arg);
      }
    }
    return result;
  }
  
  /**
   * COUNTIF helper
   */
  private countIf(range: any[], criteria: string): number {
    const evaluator = this.parseCriteria(criteria);
    return range.filter(v => evaluator(v)).length;
  }
  
  /**
   * SUMIF helper
   */
  private sumIf(criteriaRange: any[], criteria: string, sumRange: any[]): number {
    const evaluator = this.parseCriteria(criteria);
    let sum = 0;
    
    for (let i = 0; i < criteriaRange.length; i++) {
      if (evaluator(criteriaRange[i])) {
        sum += Number(sumRange[i]) || 0;
      }
    }
    
    return sum;
  }
  
  /**
   * AVERAGEIF helper
   */
  private averageIf(criteriaRange: any[], criteria: string, avgRange: any[]): number {
    const evaluator = this.parseCriteria(criteria);
    let sum = 0;
    let count = 0;
    
    for (let i = 0; i < criteriaRange.length; i++) {
      if (evaluator(criteriaRange[i])) {
        sum += Number(avgRange[i]) || 0;
        count++;
      }
    }
    
    return count > 0 ? sum / count : 0;
  }
  
  /**
   * Parse criteria string (e.g., ">100", "=Apple", ">=50")
   */
  private parseCriteria(criteria: string): (value: any) => boolean {
    const match = criteria.match(/^(>=?|<=?|=|<>)(.+)$/);
    
    if (!match) {
      // Exact match
      return (v) => v == criteria;
    }
    
    const [, op, value] = match;
    const numValue = Number(value);
    
    switch (op) {
      case '>': return (v) => Number(v) > numValue;
      case '>=': return (v) => Number(v) >= numValue;
      case '<': return (v) => Number(v) < numValue;
      case '<=': return (v) => Number(v) <= numValue;
      case '=': return (v) => v == value;
      case '<>': return (v) => v != value;
      default: return () => false;
    }
  }
  
  /**
   * VLOOKUP helper
   */
  private vlookup(lookupValue: any, tableArray: any[], colIndex: number, exactMatch: boolean): any {
    // Assume tableArray is flattened from a range like A1:C10
    // Need to determine number of columns from the original range
    // For now, assume user knows the structure
    
    // Simple implementation: search first column, return value from colIndex column
    // This is a simplified version - production would need proper 2D array handling
    
    for (let i = 0; i < tableArray.length; i++) {
      const rowValue = tableArray[i];
      
      if (exactMatch) {
        if (rowValue == lookupValue) {
          // Return value from same row, colIndex positions ahead
          return tableArray[i + colIndex - 1];
        }
      } else {
        if (String(rowValue).toLowerCase().includes(String(lookupValue).toLowerCase())) {
          return tableArray[i + colIndex - 1];
        }
      }
    }
    
    return '#N/A';
  }

  /**
   * Parse function arguments
   */
  private parseArgs(argsStr: string): string[] {
    const args: string[] = [];
    let current = '';
    let depth = 0;
    let inString = false;
    let stringChar = '';

    for (let i = 0; i < argsStr.length; i++) {
      const char = argsStr[i];

      if ((char === '"' || char === "'") && !inString) {
        inString = true;
        stringChar = char;
        current += char;
      } else if (char === stringChar && inString) {
        inString = false;
        current += char;
      } else if (char === '(' && !inString) {
        depth++;
        current += char;
      } else if (char === ')' && !inString) {
        depth--;
        current += char;
      } else if (char === ',' && depth === 0 && !inString) {
        args.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    if (current.trim()) {
      args.push(current.trim());
    }

    return args;
  }

  /**
   * Evaluate cell reference
   */
  private evaluateCellRef(ref: string): any {
    const value = this.getCellValue(ref);
    if (value === undefined) {
      throw new Error(`Cell ${ref} not found`);
    }
    return value;
  }

  /**
   * Check if expression has operators
   */
  private hasOperator(expr: string): boolean {
    // Simple check for operators (excluding those in strings/functions)
    return /[\+\-\*\/\%\^\<\>\=\&]/.test(expr);
  }

  /**
   * Evaluate operation (simple left-to-right, no precedence yet)
   */
  private evaluateOperation(expr: string): any {
    // Handle parentheses first
    if (expr.includes('(')) {
      expr = this.evaluateParentheses(expr);
    }

    // Simple operator evaluation (left to right)
    // In production, would need proper operator precedence

    // Multiplication and division first
    if (expr.includes('*') || expr.includes('/')) {
      return this.evaluateMulDiv(expr);
    }

    // Addition and subtraction
    if (expr.includes('+') || expr.includes('-')) {
      return this.evaluateAddSub(expr);
    }

    // Comparison operators
    if (expr.includes('=') || expr.includes('>') || expr.includes('<')) {
      return this.evaluateComparison(expr);
    }

    return this.evaluateExpression(expr);
  }

  /**
   * Evaluate parentheses
   */
  private evaluateParentheses(expr: string): string {
    // Simple parentheses evaluation
    while (expr.includes('(')) {
      const start = expr.lastIndexOf('(');
      const end = expr.indexOf(')', start);
      const inner = expr.substring(start + 1, end);
      const result = this.evaluateExpression(inner);
      expr = expr.substring(0, start) + result + expr.substring(end + 1);
    }
    return expr;
  }

  /**
   * Evaluate multiplication and division
   */
  private evaluateMulDiv(expr: string): number {
    const tokens = expr.split(/([*\/])/);
    let result = Number(this.evaluateExpression(tokens[0]));

    for (let i = 1; i < tokens.length; i += 2) {
      const op = tokens[i];
      const val = Number(this.evaluateExpression(tokens[i + 1]));

      if (op === '*') {
        result *= val;
      } else if (op === '/') {
        if (val === 0) throw new Error(FormulaError.DIV0);
        result /= val;
      }
    }

    return result;
  }

  /**
   * Evaluate addition and subtraction
   */
  private evaluateAddSub(expr: string): number {
    const tokens = expr.split(/([+\-])/);
    let result = Number(this.evaluateExpression(tokens[0]));

    for (let i = 1; i < tokens.length; i += 2) {
      const op = tokens[i];
      const val = Number(this.evaluateExpression(tokens[i + 1]));

      if (op === '+') {
        result += val;
      } else if (op === '-') {
        result -= val;
      }
    }

    return result;
  }

  /**
   * Evaluate comparison
   */
  private evaluateComparison(expr: string): boolean {
    if (expr.includes('=')) {
      const [left, right] = expr.split('=').map(s => s.trim());
      return this.evaluateExpression(left) === this.evaluateExpression(right);
    }
    if (expr.includes('>')) {
      const [left, right] = expr.split('>').map(s => s.trim());
      return this.evaluateExpression(left) > this.evaluateExpression(right);
    }
    if (expr.includes('<')) {
      const [left, right] = expr.split('<').map(s => s.trim());
      return this.evaluateExpression(left) < this.evaluateExpression(right);
    }
    return false;
  }
}

